import React, { useEffect, useState } from "react";
import { X, Cake, PartyPopper, Trophy, Sparkles } from "lucide-react";
import { K } from "../theme";
import { Av } from "./Avatar";
import { Bt } from "./Button";
import api from "../api";

// Opens when someone taps a "celebration" notification — the receiving
// end of Send Wishes (birthday / anniversary / Employee of the Month).
// Shows who sent it (name + photo) rather than a bare system message, with
// a small celebratory animation, matching each card's own accent color on
// the Dashboard so it reads as the same feature, not a generic popup.
const TYPE_META = {
  birthday:    { Icon: Cake,        verb: "wished you a Happy Birthday",              grad: `linear-gradient(135deg, ${K.ro} 0%, #e11d48 100%)`, c: K.ro,
                 note: "Wishing you all the very best — enjoy your day!" },
  anniversary: { Icon: PartyPopper, verb: "wished you a Happy Work Anniversary",       grad: `linear-gradient(135deg, ${K.pu} 0%, #7c3aed 100%)`, c: K.pu,
                 note: "Thank you for everything you bring to the team — here's to many more." },
  eom:         { Icon: Trophy,      verb: "congratulated you on Employee of the Month", grad: `linear-gradient(135deg, ${K.a} 0%, #b45309 100%)`, c: K.a,
                 note: "Your hard work hasn't gone unnoticed — congratulations on this well-deserved recognition!" },
};

// Deterministic confetti so pieces don't jump around on re-render.
const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: ((i * 53) % 25) / 10,
  dur: 2.6 + ((i * 17) % 20) / 10,
  size: 6 + (i % 4) * 2,
  color: ["#fff", "#fde047", "#67e8f9", "#86efac", "#fda4af", "#c4b5fd"][i % 6],
  round: i % 3 === 0,
}));

export const WishReceivedModal = ({ item, onClose }) => {
  const [state, setState] = useState("idle"); // idle | sending | sent
  useEffect(() => { setState("idle"); }, [item]);
  if (!item) return null;
  const canThank = item.fromId && item.fromId !== "system";
  // "Thank You" tells the sender (a bell notification on their side), then
  // closes. No sender (automatic "Team Zucol" wish) → just closes.
  const thank = async () => {
    if (!canThank || state !== "idle") return onClose();
    setState("sending");
    try { await api.post(`/api/employees/${encodeURIComponent(item.fromId)}/wish-thanks`, { type: item.type }); } catch { /* thanks is best-effort */ }
    setState("sent");
    setTimeout(onClose, 1100);
  };
  const meta = TYPE_META[item.type] || TYPE_META.birthday;
  const Icon = meta.Icon;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 backdrop-blur-sm z-[260] flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b0 rounded-2xl w-full max-w-[420px] overflow-hidden shadow-2xl" onMouseDown={e => e.stopPropagation()}>
        <div className="relative p-7 text-white text-center overflow-hidden" style={{ background: meta.grad }}>
          <style>{`@keyframes wishFall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(260px) rotate(540deg); opacity: 0; } }
@keyframes wishPop { 0% { transform: scale(.6); opacity: 0; } 70% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }`}</style>
          {CONFETTI.map((c, i) => (
            <span key={i} className="absolute top-0 pointer-events-none" style={{ left: `${c.left}%`, width: c.size, height: c.round ? c.size : c.size * 1.6, background: c.color, borderRadius: c.round ? "50%" : 2, opacity: 0, animation: `wishFall ${c.dur}s linear ${c.delay}s infinite` }} />
          ))}
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-8 -bottom-8 w-28 h-28 rounded-full bg-white/10" />
          <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1 border-0 cursor-pointer transition-colors">
            <X size={16} />
          </button>

          <div className="relative inline-block mb-3">
            <div className="absolute inset-0 rounded-full blur-lg opacity-60 pointer-events-none animate-pulse" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.65), transparent 70%)", transform: "scale(1.5)" }} />
            <div className="relative w-16 h-16 rounded-full p-[3px] bg-white/25 backdrop-blur-md shadow-lg mx-auto">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                <Av nm={item.fromName || "Team Zucol"} sz={58} rn={meta.c} src={item.fromPhoto} />
              </div>
            </div>
            <Sparkles size={16} className="absolute -top-1 -right-1 text-white animate-bounce" />
          </div>

          <div className="text-[15px] font-bold m-0 truncate max-w-[280px] mx-auto">{item.fromName || "Team Zucol"}</div>
          <div className="text-white/85 text-[13px] mt-1">{meta.verb} 🎉</div>
        </div>

        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3" style={{ background: `${meta.c}1a` }}>
            <Icon size={20} color={meta.c} />
          </div>
          <div className="text-[14px] text-t2 leading-relaxed" style={{ animation: "wishPop .5s ease-out both" }}>{meta.note}</div>
          <Bt full disabled={state === "sending"} style={{ background: state === "sent" ? "#16a34a" : meta.c, marginTop: 20 }} onClick={thank}>
            {state === "sent" ? "Thanks sent ✓" : state === "sending" ? "Sending…" : "Thank You 🙏"}
          </Bt>
          {canThank && state === "idle" && <div className="text-[11px] text-t3 mt-2">{item.fromName} will be notified</div>}
        </div>
      </div>
    </div>
  );
};
